import { useContext, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import PropTypes from 'prop-types'

import { Tooltip } from 'react-tooltip'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faBatteryHalf,
	faBatteryFull,
	faBatterySlash,
	faChevronDown,
	faChevronUp,
	faImage,
	faCalendarWeek,
} from '@fortawesome/pro-solid-svg-icons'
import { SystemContext, UserContext } from '../../../../shared/context'
import { fetchPicture } from '../../../../helper/fetch/Attachment'
import LoadingPulse from '../../../../components/Loading/LoadingPulse'
import useRequest from '../../../../hooks/useRequest'

const POWERED_THRESHOLD_MINUTES = 60

const RowName = ({ device }) => {
	const { setDialogStructure, openDialogHandler } = useContext(SystemContext)
	const { token } = useContext(UserContext)
	const [isExtended, setIsExtended] = useState(false)
	const [battery, setBattery] = useState(null)

	const { Request } = useRequest()

	const showPicture = async e => {
		e.stopPropagation()

		let dialogModal = {
			header: device.name,
			body: <LoadingPulse />,
		}

		openDialogHandler(true)
		setDialogStructure(dialogModal)
		const picture = await fetchPicture(Request, token, device.id)

		if (picture) {
			dialogModal.body = (
				<div className="flex justify-center">
					<img
						className="max-h-dialogImg max-w-dialogImg"
						src={picture.content}
						alt={device.name}
					/>
				</div>
			)
			setDialogStructure({ ...dialogModal })
		} else {
			openDialogHandler(false)
		}
	}

	const batteryColor = bat => {
		if (isNaN(bat)) return { color: 'grey', icon: faBatteryFull, bat }
		if (bat > 40) return { color: 'green', icon: faBatteryFull, bat }
		if (bat > 20) return { color: 'orange', icon: faBatteryHalf, bat }
		return { color: 'red', icon: faBatteryHalf, bat }
	}

	const getTimestampCandidates = lastTimestamp => {
		if (!lastTimestamp) return []

		if (typeof lastTimestamp === 'number') {
			return [lastTimestamp < 1000000000000 ? lastTimestamp * 1000 : lastTimestamp]
		}

		const timestamp = lastTimestamp.toString().trim()
		if (!timestamp) return []

		if (/^\d+$/.test(timestamp)) {
			const numericTimestamp = Number(timestamp)
			return [
				numericTimestamp < 1000000000000
					? numericTimestamp * 1000
					: numericTimestamp,
			]
		}

		const normalizedTimestamp = timestamp.replace(' ', 'T')
		const candidates = [Date.parse(normalizedTimestamp)]
		const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedTimestamp)

		if (!hasTimezone) {
			candidates.push(Date.parse(normalizedTimestamp + 'Z'))
		}

		return candidates.filter(candidate => !isNaN(candidate))
	}

	const powerStatus = lastTimestamp => {
		const now = new Date().getTime()
		const isPowered = getTimestampCandidates(lastTimestamp).some(timestamp => {
			const minutesSinceLastSeen = (now - timestamp) / 1000 / 60
			return (
				minutesSinceLastSeen >= 0 &&
				minutesSinceLastSeen < POWERED_THRESHOLD_MINUTES
			)
		})

		return {
			color: isPowered ? 'green' : 'grey',
			icon: faBatterySlash,
			content: isPowered
				? 'Dieses Gerät ist stromversorgt.'
				: 'Dieses Gerät scheint offline zu sein.',
		}
	}

	useEffect(() => {
		if (device.isIoModule) {
			setBattery(powerStatus(device.attributes?.last_timestamp))
			return
		}

		setBattery(batteryColor(device.battery))
	}, [device.attributes?.last_timestamp, device.battery, device.isIoModule])

	const toggleExpanded = () => {
		setIsExtended(current => !current)
	}

	const handleToggleKeyDown = e => {
		if (e.target !== e.currentTarget) {
			return
		}

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			toggleExpanded()
		}
	}

	return (
		<div
			className="flex flex-col items-center justify-center w-full h-full text-sm cursor-pointer"
			onClick={toggleExpanded}
			onKeyDown={handleToggleKeyDown}
			role="button"
			tabIndex={0}
			aria-expanded={isExtended}
		>
			<div className="flex items-center justify-between w-full">
				<div
					className={
						'truncate' +
						(device.isIoModule && device.alarm_active !== '1' ? ' text-gray-400' : '')
					}
				>
					{device.name}
				</div>
				<FontAwesomeIcon icon={isExtended ? faChevronUp : faChevronDown} />
			</div>
			{isExtended && (
				<div className="flex items-center justify-center w-full gap-2 mt-1">
					{battery && (
						<div
							data-tooltip-id={'battery-tooltip-' + device.id}
							className="flex items-center justify-center"
						>
							<FontAwesomeIcon
								icon={battery.icon}
								color={battery.color}
								className="w-5 h-5"
							/>
						</div>
					)}
					<div className="text-xs italic grow">{device.serial}</div>
					<button
						type="button"
						onClick={showPicture}
						className="flex items-center justify-center"
						aria-label={`Installationsfoto von ${device.name} anzeigen`}
					>
						<FontAwesomeIcon
							data-tooltip-id={'picture-tooltip-' + device.id}
							icon={faImage}
							className="w-4 h-4"
						/>
					</button>
					{/* <i className="fa-kit fa-solid-memo-magnifying-glass fa-lg" /> */}

					<NavLink
						to={'../week?deviceid=' + device.id}
						onClick={e => e.stopPropagation()}
					>
						<div className="flex items-center justify-center">
							<FontAwesomeIcon
								data-tooltip-id={'showweek-tooltip-' + device.id}
								icon={faCalendarWeek}
								className="w-4 h-4"
							/>
						</div>
					</NavLink>

					<Tooltip
						id={'battery-tooltip-' + device.id}
						content={
							device.isIoModule
								? battery.content
								: isNaN(battery.bat)
									? 'nicht verfügbar'
									: battery.bat + ' %'
						}
					/>
					<Tooltip id={'picture-tooltip-' + device.id} content="Installationsfoto" />
					<Tooltip id={'showweek-tooltip-' + device.id} content="Wochenansicht" />
				</div>
			)}
		</div>
	)
}

RowName.propTypes = {
	device: PropTypes.object,
}

export default RowName
